import { useEffect, useState } from "react";
import type { LoadingConfig } from "~/theme/types";

interface LoadingProps extends LoadingConfig {}

export function Loading({ assets, videoAssets, logoAlt, logoSrc }: LoadingProps) {
  const [visible, setVisible] = useState(true);
  const [fadeOut, setFadeOut] = useState(false);
  const [progress, setProgress] = useState(0);
  const videoCount = videoAssets?.length ?? 0;
  const totalAssets = assets.length + videoCount || 1;

  useEffect(() => {
    let loaded = 0;
    let done = false;
    const videoEls: HTMLVideoElement[] = [];

    const finish = () => {
      if (done) return;
      done = true;
      setTimeout(() => {
        setFadeOut(true);
        setTimeout(() => setVisible(false), 700);
      }, 300);
    };

    const onLoad = () => {
      loaded++;
      setProgress(Math.round((loaded / totalAssets) * 100));
      if (loaded === totalAssets) finish();
    };

    if (assets.length === 0 && videoCount === 0) {
      finish();
      return;
    }

    assets.forEach((src) => {
      let fired = false;
      const fire = () => {
        if (fired) return;
        fired = true;
        onLoad();
      };
      const img = new Image();
      img.onload = fire;
      img.onerror = fire;
      img.src = src;
      // 캐시된 이미지는 src 할당 직후 이미 complete 일 수 있어 onload 가 안 뜬다.
      if (img.complete) fire();
    });

    videoAssets?.forEach((src) => {
      let fired = false;
      const fire = () => {
        if (fired) return;
        fired = true;
        onLoad();
      };
      const v = document.createElement("video");
      v.preload = "auto";
      v.muted = true;
      v.addEventListener("loadeddata", fire, { once: true });
      v.addEventListener("loadedmetadata", fire, { once: true });
      v.addEventListener("canplay", fire, { once: true });
      v.addEventListener("error", fire, { once: true });
      v.src = src;
      // 캐시된 영상은 readyState 가 빠르게 채워질 수 있다.
      if (v.readyState >= 2) fire();
      videoEls.push(v);
    });

    const timeout = setTimeout(() => {
      setProgress(100);
      finish();
    }, 10000);

    return () => {
      clearTimeout(timeout);
      videoEls.forEach((v) => {
        v.removeAttribute("src");
        v.load();
      });
    };
  }, [assets, videoAssets, totalAssets, videoCount]);

  if (!visible) return null;

  return (
    <div
      className={`loading fixed inset-0 z-[9999] flex items-center justify-center transition-opacity duration-700 bg-[var(--ip-bg)] ${
        fadeOut ? "pointer-events-none opacity-0" : ""
      }`}
    >
      <div className="loading__inner flex flex-col items-center gap-6">
        <img
          src={logoSrc}
          alt={logoAlt}
          className="loading__logo h-14 w-auto animate-[loadingLogoIn_0.8s_ease_0.2s_forwards] opacity-0"
        />
        <div className="loading__bar-wrap h-px w-[200px] overflow-hidden rounded-[1px] bg-[rgba(var(--ip-primary-glow),0.2)]">
          <div
            className="loading__bar h-full rounded-[1px] transition-[width] duration-300 ease-out bg-[var(--ip-primary)]"
            style={{ width: `${progress}%` }}
          />
        </div>
        <p className="loading__num font-mono text-[11px] tracking-[0.2em] text-[var(--ip-primary)]">
          {String(progress).padStart(3, "0")}
        </p>
      </div>
    </div>
  );
}
